import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  Users, 
  FileDown, 
  Plus,
  ArrowRight,
  Loader2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    conferenceSets: 0,
    scheduleSets: 0,
    teachers: 0,
    exportFiles: 0
  });
  const [recentConferenceSets, setRecentConferenceSets] = useState([]);
  const [loading, setLoading] = useState(true);

  const subscriptionExpired = user?.subscription_expired === true;
  const isSchoolAdmin = user?.role === 'school_admin';

  useEffect(() => {
    // Redirect expired school admins to settings
    if (subscriptionExpired && isSchoolAdmin) {
      navigate('/school-settings');
      return;
    }

    const fetchDashboardData = async () => {
      try {
        const [confSets, schedSets, teachers, exports] = await Promise.all([
          api.get('/conference-sets'),
          api.get('/schedule-sets'),
          api.get('/teachers'),
          api.get('/export-files')
        ]);

        setStats({
          conferenceSets: confSets.data.length,
          scheduleSets: schedSets.data.length,
          teachers: teachers.data.length,
          exportFiles: exports.data.length
        });

        setRecentConferenceSets(confSets.data.slice(0, 5));
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [subscriptionExpired, isSchoolAdmin, navigate]);

  const statCards = [
    {
      title: 'Conference Sets',
      value: stats.conferenceSets,
      icon: Calendar,
      color: 'bg-indigo-500',
      link: '/conference-sets'
    },
    {
      title: 'Schedule Sets',
      value: stats.scheduleSets,
      icon: Clock,
      color: 'bg-emerald-500',
      link: '/schedule-sets'
    },
    {
      title: 'Teachers',
      value: stats.teachers,
      icon: Users,
      color: 'bg-amber-500',
      link: '/teachers'
    },
    {
      title: 'Export Files',
      value: stats.exportFiles,
      icon: FileDown,
      color: 'bg-rose-500',
      link: '/exports'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="school-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900">
              Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">
              Manage your parent-teacher conferences
            </p>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
            onClick={() => navigate('/conference-sets/new')}
            data-testid="create-conference-set-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conference Set
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statCards.map((stat) => (
            <Card 
              key={stat.title} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-2 sm:p-3 rounded-lg`}>
                    <stat.icon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Conference Sets */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-outfit text-lg">Recent Conference Sets</CardTitle>
              <CardDescription className="text-sm">Your most recent conference configurations</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/conference-sets')}
              data-testid="view-all-conference-sets-btn"
              className="w-full sm:w-auto"
            >
              View All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentConferenceSets.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No conference sets yet</p>
                <Button 
                  className="mt-4"
                  variant="outline"
                  onClick={() => navigate('/conference-sets/new')}
                >
                  Create your first conference set
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentConferenceSets.map((cs) => (
                  <div 
                    key={cs.id}
                    className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/conference-sets/${cs.id}`)}
                    data-testid={`conference-set-${cs.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 text-sm sm:text-base truncate">{cs.friendly_name}</p>
                      <p className="text-xs sm:text-sm text-slate-500 truncate">
                        {cs.school_level_description} · {cs.teacher_ids?.length || 0} teachers
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0 ml-2" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/teachers')}>
            <CardContent className="p-4 sm:p-6 text-center">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 mx-auto mb-2 sm:mb-3" />
              <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Sync Teachers</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">Import from Veracross</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/schedule-sets/new')}>
            <CardContent className="p-4 sm:p-6 text-center">
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600 mx-auto mb-2 sm:mb-3" />
              <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Create Schedule</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">Build time slots</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/exports')}>
            <CardContent className="p-4 sm:p-6 text-center">
              <FileDown className="h-6 w-6 sm:h-8 sm:w-8 text-rose-600 mx-auto mb-2 sm:mb-3" />
              <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Export Data</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">Download CSV files</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
